import React, { useEffect, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./CustomerIdentity.css";

const API_URL = `${process.env.REACT_APP_URL}customer_identity/submit`;
const BRANCH_API_URL = `${process.env.REACT_APP_URL}customer_identity/branches`;

const CustomerIdentity = () => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const [identityStatus, setIdentityStatus] = useState("");

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesError, setBranchesError] = useState("");

  const [formData, setFormData] = useState({
    branch_id: "",
    account_holder_name: "",
    account_number: "",
    date_of_birth: "",
    nid_number: "",
    email: "",
  });

  const [files, setFiles] = useState({
    nid_front_file: null,
    nid_back_file: null,
    citizenship_file: null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [apiError, setApiError] = useState("");

  // =========================================================
  // FETCH BRANCHES
  // =========================================================

  useEffect(() => {
    const fetchBranches = async () => {
      setBranchesLoading(true);
      setBranchesError("");

      try {
        const response = await fetch(BRANCH_API_URL, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        const result = await response.json();

        if (!response.ok || result.status !== "success") {
          throw new Error(result.status_message || "Unable to load branches.");
        }

        setBranches(result.data?.branches || []);
      } catch (error) {
        console.error("Branch Fetch Error:", error);

        setBranches([]);
        setBranchesError(
          error.message || "Unable to load branches. Please try again.",
        );
      } finally {
        setBranchesLoading(false);
      }
    };

    fetchBranches();
  }, []);

  // =========================================================
  // FORMAT NID
  // 10 digits -> 000-000-000-0
  // =========================================================

  const formatNidNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);

    const parts = [];

    if (digits.length > 0) {
      parts.push(digits.substring(0, 3));
    }

    if (digits.length > 3) {
      parts.push(digits.substring(3, 6));
    }

    if (digits.length > 6) {
      parts.push(digits.substring(6, 9));
    }

    if (digits.length > 9) {
      parts.push(digits.substring(9, 10));
    }

    return parts.join("-");
  };

  // =========================================================
  // HANDLE NORMAL INPUT CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setApiError("");
  };

  // =========================================================
  // HANDLE ACCOUNT HOLDER NAME
  // Only alphabets + spaces
  // =========================================================

  const handleAccountHolderNameChange = (e) => {
    const value = e.target.value;

    const cleanedValue = value.replace(/[^\p{L}\p{M} ]/gu, "");

    setFormData((prev) => ({
      ...prev,
      account_holder_name: cleanedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      account_holder_name: "",
    }));

    setApiError("");
  };

  // =========================================================
  // HANDLE ACCOUNT NUMBER
  // Only 14 digits
  // =========================================================

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 14);

    setFormData((prev) => ({
      ...prev,
      account_number: value,
    }));

    setErrors((prev) => ({
      ...prev,
      account_number: "",
    }));

    setApiError("");
  };

  // =========================================================
  // HANDLE NID NUMBER
  // User types only numbers
  // Automatically formats:
  // 1234567890 -> 123-456-789-0
  // =========================================================

  const handleNidChange = (e) => {
    const formattedValue = formatNidNumber(e.target.value);

    setFormData((prev) => ({
      ...prev,
      nid_number: formattedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      nid_number: "",
    }));

    setApiError("");
  };

  // =========================================================
  // HANDLE IDENTITY STATUS
  // =========================================================

  const handleIdentityStatus = (status) => {
    setIdentityStatus(status);

    setFormData({
      branch_id: "",
      account_holder_name: "",
      account_number: "",
      date_of_birth: "",
      nid_number: "",
      email: "",
    });

    setFiles({
      nid_front_file: null,
      nid_back_file: null,
      citizenship_file: null,
    });

    setErrors({});
    setApiError("");
    setSuccessData(null);

    // Clear file inputs
    document.querySelectorAll('input[type="file"]').forEach((input) => {
      input.value = "";
    });
  };

  // =========================================================
  // HANDLE FILE
  // =========================================================

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    const file = selectedFiles?.[0];

    if (!file) {
      return;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setApiError("");

    // Maximum 1 MB
    if (file.size > 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        [name]: "File size must not exceed 1 MB.",
      }));

      e.target.value = "";
      return;
    }

    const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];

    const extension = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Only JPG, JPEG, PNG and PDF files are allowed.",
      }));

      e.target.value = "";
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [name]: file,
    }));
  };

  // =========================================================
  // VALIDATE FORM
  // =========================================================

  const validateForm = () => {
    const newErrors = {};

    // Identity
    if (!identityStatus) {
      newErrors.identity_status = "Please select an identity option.";
    }

    // Branch
    if (!formData.branch_id) {
      newErrors.branch_id = "Branch is required.";
    }

    // Account Holder Name
    const accountHolderName = formData.account_holder_name.trim();

    if (!accountHolderName) {
      newErrors.account_holder_name = "Account holder name is required.";
    } else if (!/^[\p{L}\p{M} ]+$/u.test(accountHolderName)) {
      newErrors.account_holder_name =
        "Account holder name can contain alphabets and spaces only.";
    }

    // Account Number
    const accountNumber = formData.account_number.trim();

    if (!accountNumber) {
      newErrors.account_number = "Account number is required.";
    } else if (!/^\d{14}$/.test(accountNumber)) {
      newErrors.account_number = "Account number must be exactly 14 digits.";
    }

    // Date of Birth
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = "Date of birth is required.";
    } else {
      const selectedDate = new Date(`${formData.date_of_birth}T00:00:00`);

      const today = new Date();

      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.date_of_birth = "Date of birth cannot be a future date.";
      }
    }

    // Email optional
    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      newErrors.email = "Enter a valid email address.";
    }

    // NID Available
    if (identityStatus === "NID_AVAILABLE") {
      const nidNumber = formData.nid_number.trim();

      if (!nidNumber) {
        newErrors.nid_number = "NID number is required.";
      } else if (!/^\d{3}-\d{3}-\d{3}-\d$/.test(nidNumber)) {
        newErrors.nid_number =
          "NID number must be in the format 000-000-000-0.";
      }

      if (!files.nid_front_file) {
        newErrors.nid_front_file = "NID front side document is required.";
      }

      if (!files.nid_back_file) {
        newErrors.nid_back_file = "NID back side document is required.";
      }
    }

    // NID Not Available
    if (identityStatus === "NID_NOT_AVAILABLE") {
      if (!files.citizenship_file) {
        newErrors.citizenship_file = "Citizenship document is required.";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSuccessData(null);
    setApiError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      data.append("identity_status", identityStatus);
      data.append("branch_id", formData.branch_id);
      data.append("account_holder_name", formData.account_holder_name.trim());
      data.append("account_number", formData.account_number.trim());

      // DOB required for BOTH options
      data.append("date_of_birth", formData.date_of_birth);

      data.append("email", formData.email.trim());

      // NID Available
      if (identityStatus === "NID_AVAILABLE") {
        data.append("nid_number", formData.nid_number.trim());
        data.append("nid_front_file", files.nid_front_file);
        data.append("nid_back_file", files.nid_back_file);
      }

      // NID Not Available
      if (identityStatus === "NID_NOT_AVAILABLE") {
        data.append("citizenship_file", files.citizenship_file);
      }

      const response = await fetch(API_URL, {
        method: "POST",
        body: data,
      });

      const result = await response.json();

      console.log("API Response:", result);

      if (!response.ok || result.status !== "success") {
        throw new Error(result.status_message || "Unable to submit the form.");
      }

      // =====================================================
      // SUCCESS
      // =====================================================

      setSuccessData({
        message:
          result.status_message || "Identity details submitted successfully.",
        reference_no: result.data?.reference_no || "",
      });

      setApiError("");

      // Reset
      setIdentityStatus("");

      setFormData({
        branch_id: "",
        account_holder_name: "",
        account_number: "",
        date_of_birth: "",
        nid_number: "",
        email: "",
      });

      setFiles({
        nid_front_file: null,
        nid_back_file: null,
        citizenship_file: null,
      });

      setErrors({});

      document.querySelectorAll('input[type="file"]').forEach((input) => {
        input.value = "";
      });
    } catch (error) {
      console.error("Customer Identity Error:", error);

      setSuccessData(null);

      setApiError(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // BRANCH OPTIONS FOR REACT SELECT
  // =========================================================

  const branchOptions = branches.map((branch) => ({
    value: branch.id,
    label: branch.branch_name,
  }));

  const selectedBranch =
    branchOptions.find(
      (option) => String(option.value) === String(formData.branch_id),
    ) || null;

  // =========================================================
  // CUSTOM BRANCH SELECT STYLES
  // =========================================================

  const branchSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "46px",
      borderRadius: "8px",
      borderColor: errors.branch_id
        ? "#dc3545"
        : state.isFocused
          ? "#2563eb"
          : "#d9dee7",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.1)" : "none",
      "&:hover": {
        borderColor: errors.branch_id ? "#dc3545" : "#2563eb",
      },
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "2px 12px",
    }),

    placeholder: (base) => ({
      ...base,
      color: "#6b7280",
    }),

    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),

    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#eff6ff" : "#fff",
      color: "#29313d",
      cursor: "pointer",
    }),
  };

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <div className="customer-identity-page">
      <div className="container py-10">
        <div className="row justify-content-center">
          <div className="col-xl-8 col-lg-9 col-md-11">
            {/* PAGE TITLE */}

            <div className="text-center mb-4">
              <h2 className="fw-bold">NID Update</h2>
            </div>

            {/* TOAST */}

            {(successData || apiError) && (
              <div
                className={`identity-toast ${
                  successData
                    ? "identity-toast-success"
                    : "identity-toast-error"
                }`}
              >
                <div className="identity-toast-icon">
                  {successData ? "✓" : "!"}
                </div>

                <div className="identity-toast-content">
                  <strong>
                    {successData
                      ? "Submission Successful"
                      : "Submission Failed"}
                  </strong>

                  <div>{successData ? successData.message : apiError}</div>

                  {successData?.reference_no && (
                    <small>
                      Reference No: <strong>{successData.reference_no}</strong>
                    </small>
                  )}
                </div>

                <button
                  type="button"
                  className="identity-toast-close"
                  onClick={() => {
                    setSuccessData(null);
                    setApiError("");
                  }}
                >
                  ×
                </button>
              </div>
            )}

            {/* CARD */}

            <div className="card border-0 shadow-sm">
              <div className="card-body p-4 p-md-5">
                {/* IDENTITY STATUS */}

                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Do you have a National Identity Card (NID)?
                  </label>

                  {errors.identity_status && (
                    <div className="text-danger small mb-2">
                      {errors.identity_status}
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <button
                        type="button"
                        className={`identity-option ${
                          identityStatus === "NID_AVAILABLE" ? "active" : ""
                        }`}
                        onClick={() => handleIdentityStatus("NID_AVAILABLE")}
                      >
                        <span className="option-icon">✓</span>

                        <span>
                          <strong>NID Available</strong>

                          <small>I have a National Identity Card</small>
                        </span>
                      </button>
                    </div>

                    <div className="col-md-6">
                      <button
                        type="button"
                        className={`identity-option ${
                          identityStatus === "NID_NOT_AVAILABLE" ? "active" : ""
                        }`}
                        onClick={() =>
                          handleIdentityStatus("NID_NOT_AVAILABLE")
                        }
                      >
                        <span className="option-icon">✓</span>

                        <span>
                          <strong>NID Not Available</strong>

                          <small>I do not have NID</small>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* FORM */}

                {identityStatus && (
                  <form onSubmit={handleSubmit} noValidate>
                    {/* ROW 1 */}

                    <div className="row">
                      {/* BRANCH */}

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Branch
                          <span className="text-danger">*</span>
                        </label>

                        <Select
                          options={branchOptions}
                          value={selectedBranch}
                          onChange={(selectedOption) => {
                            setFormData((prev) => ({
                              ...prev,
                              branch_id: selectedOption
                                ? selectedOption.value
                                : "",
                            }));

                            setErrors((prev) => ({
                              ...prev,
                              branch_id: "",
                            }));

                            setApiError("");
                          }}
                          isSearchable
                          isClearable
                          isLoading={branchesLoading}
                          isDisabled={branchesLoading}
                          placeholder={
                            branchesLoading
                              ? "Loading branches..."
                              : "Select or search branch"
                          }
                          noOptionsMessage={() => "No branch found"}
                          styles={branchSelectStyles}
                          classNamePrefix="branch-select"
                        />

                        {errors.branch_id && (
                          <div className="text-danger small mt-1">
                            {errors.branch_id}
                          </div>
                        )}

                        {branchesError && (
                          <div className="text-danger small mt-1">
                            {branchesError}
                          </div>
                        )}
                      </div>

                      {/* ACCOUNT HOLDER NAME */}

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Account Holder Name
                          <span className="text-danger">*</span>
                        </label>

                        <input
                          type="text"
                          name="account_holder_name"
                          className={`form-control ${
                            errors.account_holder_name ? "is-invalid" : ""
                          }`}
                          placeholder="Enter account holder name"
                          value={formData.account_holder_name}
                          onChange={handleAccountHolderNameChange}
                          maxLength={150}
                          autoComplete="name"
                        />

                        {errors.account_holder_name && (
                          <div className="invalid-feedback">
                            {errors.account_holder_name}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ROW 2 */}

                    <div className="row">
                      {/* ACCOUNT NUMBER */}

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Account Number
                          <span className="text-danger">*</span>
                        </label>

                        <input
                          type="text"
                          inputMode="numeric"
                          name="account_number"
                          className={`form-control ${
                            errors.account_number ? "is-invalid" : ""
                          }`}
                          placeholder="Enter 14 digit account number"
                          value={formData.account_number}
                          onChange={handleAccountNumberChange}
                          maxLength={14}
                          autoComplete="off"
                        />

                        {errors.account_number && (
                          <div className="invalid-feedback">
                            {errors.account_number}
                          </div>
                        )}
                      </div>

                      {/* DATE OF BIRTH */}

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Date of Birth
                          <span className="text-danger">*</span>
                        </label>

                        <div className="date-picker-wrapper">
                          <DatePicker
                            selected={
                              formData.date_of_birth
                                ? new Date(`${formData.date_of_birth}T00:00:00`)
                                : null
                            }
                            onChange={(date) => {
                              const formattedDate = date
                                ? `${date.getFullYear()}-${String(
                                    date.getMonth() + 1,
                                  ).padStart(2, "0")}-${String(
                                    date.getDate(),
                                  ).padStart(2, "0")}`
                                : "";

                              setFormData((prev) => ({
                                ...prev,
                                date_of_birth: formattedDate,
                              }));

                              setErrors((prev) => ({
                                ...prev,
                                date_of_birth: "",
                              }));

                              setApiError("");

                              // Close calendar after selecting date
                              setIsDatePickerOpen(false);
                            }}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Select date of birth"
                            maxDate={new Date()}
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            isClearable
                            readOnly
                            open={isDatePickerOpen}
                            onInputClick={() => {
                              setIsDatePickerOpen(true);
                            }}
                            onClickOutside={() => {
                              setIsDatePickerOpen(false);
                            }}
                            onCalendarClose={() => {
                              setIsDatePickerOpen(false);
                            }}
                            className={`form-control ${
                              errors.date_of_birth ? "is-invalid" : ""
                            }`}
                            wrapperClassName="date-picker-input-wrapper"
                            autoComplete="off"
                          />

                          <span
                            className="date-picker-icon"
                            onClick={() => {
                              setIsDatePickerOpen(true);
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setIsDatePickerOpen(true);
                              }
                            }}
                          >
                            📅
                          </span>
                        </div>

                        {errors.date_of_birth && (
                          <div className="invalid-feedback d-block">
                            {errors.date_of_birth}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ROW 3 */}

                    <div className="row">
                      {/* NID NUMBER */}

                      {identityStatus === "NID_AVAILABLE" && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            NID Number
                            <span className="text-danger">*</span>
                          </label>

                          <input
                            type="text"
                            inputMode="numeric"
                            name="nid_number"
                            className={`form-control ${
                              errors.nid_number ? "is-invalid" : ""
                            }`}
                            placeholder="Enter NID number"
                            value={formData.nid_number}
                            onChange={handleNidChange}
                            maxLength={13}
                            autoComplete="off"
                          />

                          {errors.nid_number && (
                            <div className="invalid-feedback">
                              {errors.nid_number}
                            </div>
                          )}
                        </div>
                      )}

                      {/* EMAIL */}

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Email Address
                          <small className="text-muted ms-1">(Optional)</small>
                        </label>

                        <input
                          type="email"
                          name="email"
                          className={`form-control ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          placeholder="Enter your email address"
                          value={formData.email}
                          onChange={handleChange}
                          maxLength={150}
                          autoComplete="email"
                        />

                        {errors.email && (
                          <div className="invalid-feedback">{errors.email}</div>
                        )}
                      </div>
                    </div>

                    {/* NID DOCUMENTS */}

                    {identityStatus === "NID_AVAILABLE" && (
                      <>
                        <FileUpload
                          label="NID Front Side"
                          name="nid_front_file"
                          file={files.nid_front_file}
                          error={errors.nid_front_file}
                          onChange={handleFileChange}
                        />

                        <FileUpload
                          label="NID Back Side"
                          name="nid_back_file"
                          file={files.nid_back_file}
                          error={errors.nid_back_file}
                          onChange={handleFileChange}
                        />
                      </>
                    )}

                    {/* CITIZENSHIP */}

                    {identityStatus === "NID_NOT_AVAILABLE" && (
                      <FileUpload
                        label="Original Citizenship"
                        name="citizenship_file"
                        file={files.citizenship_file}
                        error={errors.citizenship_file}
                        onChange={handleFileChange}
                      />
                    )}

                    {/* SECURITY NOTICE */}

                    <div className="security-notice mb-4">
                      <div className="security-icon">🔒</div>

                      <div>
                        <strong>Your information is secure</strong>

                        <p>
                          Your identity documents will be securely stored and
                          used only for customer record updates.
                        </p>
                      </div>
                    </div>

                    {/* SUBMIT */}

                    <button
                      type="submit"
                      className="btn btn-primary w-100 py-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          Submitting...
                        </>
                      ) : (
                        "Submit Identity Details"
                      )}
                    </button>
                  </form>
                )}

                {/* INITIAL MESSAGE */}

                {!identityStatus && !successData && (
                  <div className="text-center py-4 text-muted">
                    <div className="fs-1 mb-2">🪪</div>

                    <p className="mb-0">Select an option above to continue.</p>
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER NOTE */}

            <div className="text-center mt-3">
              <small className="text-muted">
                Maximum document size: 1 MB. Allowed formats: JPG, JPEG, PNG and
                PDF.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// FILE UPLOAD COMPONENT
// =============================================================

const FileUpload = ({ label, name, file, error, onChange }) => {
  return (
    <div className="mb-4">
      <label className="form-label">
        {label}

        <span className="text-danger">*</span>
      </label>

      <div className={`upload-box ${error ? "upload-error" : ""}`}>
        <input
          type="file"
          name={name}
          id={name}
          className="d-none"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={onChange}
        />

        <label htmlFor={name} className="upload-label">
          <div className="upload-icon">📄</div>

          <div>
            {file ? (
              <>
                <strong>{file.name}</strong>

                <small>Click to replace file</small>
              </>
            ) : (
              <>
                <strong>Click to upload</strong>

                <small>JPG, JPEG, PNG or PDF (Maximum 1 MB)</small>
              </>
            )}
          </div>
        </label>
      </div>

      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

export default CustomerIdentity;
